import React, { useCallback, useEffect, useState } from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  ArrowRight,
  Activity,
  Accessibility,
  BellRing,
  Building2,
  Check,
  ChevronLeft,
  CalendarDays,
  CircleAlert,
  Clock3,
  LogOut,
  MapPin,
  Menu,
  Pause,
  Pencil,
  Play,
  Plus,
  QrCode,
  RefreshCw,
  Search,
  ShieldCheck,
  UserPlus,
  UserCircle,
  Users,
  X,
} from "lucide-react";
import { api, apiV1 } from "./api";
import { useAuth } from "./contexts/AuthContext";
import { enablePush, hasPushSubscription } from "./push";
import "./App.css";
import QueMeLogo from "./components/brand/QueMeLogo";
import LandingPage from "./components/landing/LandingPage";
import Seo from "./components/Seo";
import CookieBanner from "./components/CookieBanner";
import { NotFound, PrivacyPolicy, TermsAndConditions, ThankYou } from "./pages/LegalPages";
import { trackEvent } from "./analytics";
import EventForm from "./pages/EventForm";
import Events from "./pages/Events";

const labels = {
  draft: "Draft",
  open: "Open",
  paused: "Paused",
  closed: "Closed",
  waiting: "Waiting",
  called: "Your turn",
  serving: "Being served",
  completed: "Completed",
  cancelled: "Cancelled",
  no_show: "No-show",
  organizer: "Owner",
  staff: "Staff",
};
const roleLabels = {
  organizer: "Business owner",
  staff: "Business staff",
  customer: "Customer",
};
const roleDisplay = {
  organizer: "Organizer · Business owner",
  staff: "Staff · Business staff",
  customer: "Customer",
};
const Pill = ({ value }) => (
  <span className={"pill " + value}>{labels[value] || value}</span>
);
const Banner = ({ children, tone = "info" }) => (
  <div className={"banner " + tone}>
    <CircleAlert size={18} />
    {children}
  </div>
);
const Loading = () => (
  <div className="center-state">
    <RefreshCw className="spin" />
    <p>Loading QueMe…</p>
  </div>
);
function GoogleAuthButton({ onSuccess }) {
  const container = React.useRef(null);
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  useEffect(() => {
    if (!clientId || !container.current) return undefined;
    const element = container.current;
    let attempts = 0;
    const render = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => onSuccess(response.credential),
          use_fedcm_for_button: true,
          button_auto_select: false,
        });
        window.google.accounts.id.renderButton(element, {
          theme: "outline",
          size: "large",
          width: "360",
          shape: "pill",
          logo_alignment: "left",
          text: "continue_with",
        });
        return;
      }
      if (attempts++ < 50) window.setTimeout(render, 100);
    };
    render();
    return () => {
      element.innerHTML = "";
    };
  }, [clientId, onSuccess]);
  if (!clientId) return null;
  return (
    <div
      className="google-auth-button"
      ref={container}
      aria-label="Continue with Google"
    />
  );
}
function CustomerShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const isBusiness = user && user.role !== "customer";
  return (
    <>
      <header className="topbar customer-topbar">
        <Link to="/" className="brand">
          <QueMeLogo variant="primary" size={36} />
          <span>
            <small>Your turn. Your time.</small>
          </span>
        </Link>
        <nav>
          <Link to="/find">Explore</Link>
          {user && <Link to="/tickets">My tickets</Link>}
          {user && <Link to="/notifications">Inbox</Link>}
          {user && <span className="role-badge">{roleDisplay[user.role]}</span>}
          {isBusiness && (
            <Link className="button small listing-entry" to="/manage/events">
              My listings
            </Link>
          )}
          {user ? (
            <button className="text-button" onClick={logout}>
              <LogOut size={16} /> Sign out
            </button>
          ) : (
            <>
              <Link to="/login">Sign in</Link>
              <Link className="button small" to="/signup">
                Get started
              </Link>
            </>
          )}
        </nav>
      </header>
      <main>{children}</main>
      <nav className="mobile-bottom-nav" aria-label="Customer navigation">
        <Link to="/">Home</Link>
        <Link to="/find">
          <Search size={18} /> Explore
        </Link>
        {user && (
          <Link to="/tickets">
            <QrCode size={18} /> My tickets
          </Link>
        )}
      </nav>
      {location.pathname !== "/" && (
        <footer className="site-footer">QueMe · Simple queues for real life. <Link to="/privacy-policy">Privacy</Link> · <Link to="/terms-and-conditions">Terms</Link></footer>
      )}
    </>
  );
}
const businessNav = [
  { label: "Overview", to: "/manage", roles: ["organizer", "staff"] },
  { label: "Listings", to: "/manage/events", roles: ["organizer", "staff"] },
  {
    label: "Operations",
    to: "/manage/operations",
    roles: ["organizer", "staff"],
  },
  { label: "Team", to: "/manage/team", roles: ["organizer"] },
  { label: "Reports", to: "/manage/reports", roles: ["organizer"] },
  {
    label: "Configuration",
    to: "/manage/configuration",
    roles: ["organizer", "staff"],
  },
  { label: "Settings", to: "/manage/settings", roles: ["organizer"] },
  { label: "Billing & Plan", to: "/manage/billing", roles: ["organizer"] },
];
function BusinessShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const menuButtonRef = React.useRef(null);
  const drawerRef = React.useRef(null);
  const wasDrawerOpen = React.useRef(false);
  // The API currently returns organizer/staff. Keep the shell usable for older
  // business sessions while route guards remain the source of authorization.
  const navRole =
    String(user?.role || "organizer").toLowerCase() === "staff"
      ? "staff"
      : "organizer";
  const permittedNav = businessNav.filter((item) =>
    item.roles.includes(navRole),
  );
  const isActive = (item) =>
    location.pathname === item.to ||
    (item.to !== "/manage" && location.pathname.startsWith(item.to));

  useEffect(() => {
    setDrawerOpen(false);
    setAccountOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setDrawerOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!drawerOpen) {
      document.body.style.overflow = "";
      if (wasDrawerOpen.current) menuButtonRef.current?.focus();
      wasDrawerOpen.current = false;
      return undefined;
    }
    wasDrawerOpen.current = true;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setDrawerOpen(false);
        return;
      }
      if (event.key !== "Tab" || !drawerRef.current) return;
      const focusable = drawerRef.current.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex="0"]',
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    requestAnimationFrame(() =>
      drawerRef.current?.querySelector("button")?.focus(),
    );
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [drawerOpen]);

  useEffect(
    () => () => {
      document.body.style.overflow = "";
    },
    [],
  );

  const navigation = (className = "business-nav") => (
    <nav className={className} aria-label="Business navigation">
      {permittedNav.map((item) => (
        <Link
          className={isActive(item) ? "active" : ""}
          aria-current={isActive(item) ? "page" : undefined}
          key={item.to}
          to={item.to}
          onClick={() => setDrawerOpen(false)}
        >
          {item.label}
        </Link>
      ))}
    </nav>
  );
  return (
    <div className="business-app">
      <aside className="business-sidebar">
        <Link to="/manage" className="business-brand">
          <QueMeLogo variant="primary" size={34} />
          <span>
            <small>Business</small>
          </span>
        </Link>
        <div className="workspace-selector">
          <span className="workspace-avatar">{(user?.name || "B")[0]}</span>
          <span>
            <b>{user?.tenantId ? "Your workspace" : "Personal workspace"}</b>
            <small>Operations</small>
          </span>
          <ChevronLeft size={16} />
        </div>
        {navigation()}
        <div className="sidebar-deferred">
          <small>Next release</small>
          <span>Analytics and public display</span>
        </div>
        <button className="sidebar-signout" onClick={logout}>
          <LogOut size={16} /> Sign out
        </button>
      </aside>
      <main className="business-main">
        <header className="business-header">
          <button
            ref={menuButtonRef}
            className="business-menu-button"
            aria-label="Open business navigation"
            aria-expanded={drawerOpen}
            aria-controls="business-drawer"
            onClick={() => setDrawerOpen(true)}
          >
            <Menu size={21} />
          </button>
          <Link to="/manage" className="business-mobile-brand">
            <QueMeLogo variant="icon" size={36} />
            <span>
              <small>Business</small>
            </span>
          </Link>
          <div className="business-header-context">
            <span className="context-label">QUE ME BUSINESS</span>
            <strong>
              {location.pathname.includes("operations")
                ? "Live operations"
                : "Workspace"}
            </strong>
          </div>
          <div className="business-header-actions">
            <span className="operational-dot" />{" "}
            <span className="live-system-label">Live system</span>
            <div className="account-control">
              <button
                className="account-button"
                aria-expanded={accountOpen}
                onClick={() => setAccountOpen((open) => !open)}
              >
                <UserCircle size={20} /> <span>{user?.name}</span>
              </button>
              {accountOpen && (
                <div className="account-menu">
                  <span className="role-badge">
                    {roleDisplay[user?.role] || "Business account"}
                  </span>
                  <button onClick={logout}>
                    <LogOut size={16} /> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        {children}
      </main>
      {drawerOpen && (
        <button
          className="business-backdrop"
          aria-label="Close navigation"
          onClick={() => setDrawerOpen(false)}
        />
      )}
      <aside
        id="business-drawer"
        ref={drawerRef}
        className={drawerOpen ? "business-drawer open" : "business-drawer"}
        aria-hidden={!drawerOpen}
      >
        <div className="drawer-header">
          <Link
            to="/manage"
            className="business-brand"
            onClick={() => setDrawerOpen(false)}
          >
            <QueMeLogo variant="primary" size={34} />
            <span>
              <small>Business</small>
            </span>
          </Link>
          <button
            className="drawer-close"
            aria-label="Close business navigation"
            onClick={() => setDrawerOpen(false)}
          >
            <X size={21} />
          </button>
        </div>
        {navigation("business-nav drawer-nav")}
        <div className="drawer-account">
          <span>{user?.name}</span>
          <small>{roleLabels[user?.role] || "Business account"}</small>
        </div>
        <button className="sidebar-signout" onClick={logout}>
          <LogOut size={16} /> Sign out
        </button>
      </aside>
    </div>
  );
}
function Shell({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const isBusiness =
    user && user.role !== "customer" && location.pathname.startsWith("/manage");
  const path = location.pathname;
  useEffect(() => {
    trackEvent("page_view", { page_path: path });
  }, [path]);
  const meta = path === "/" ? ["QueMe — Virtual queues without the guesswork", "Join a queue from your phone, keep your place and arrive when it matters. QueMe helps people and teams manage waiting clearly."]
    : path === "/find" ? ["Find a queue | QueMe", "Discover open QueMe queues for restaurants, clinics, salons, services and events."]
      : path === "/login" || path === "/LoginPage" ? ["Sign in | QueMe", "Sign in to QueMe to manage your account and access your queue tickets."]
        : path === "/signup" ? ["Create an account | QueMe", "Create a QueMe account to join queues or organise a clearer waiting experience."]
          : path.startsWith("/events/") ? ["Join this queue | QueMe", "View queue details, current availability and join instructions on QueMe."]
            : ["QueMe — Virtual queues without the guesswork", "QueMe helps people join queues and helps teams manage waiting clearly."];
  return <>
    <Seo title={meta[0]} description={meta[1]} path={path} />
    {isBusiness ? <BusinessShell>{children}</BusinessShell> : <CustomerShell>{children}</CustomerShell>}
    <CookieBanner />
  </>;
}
function Guard({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role))
    return (
      <div className="center-state">
        <CircleAlert />
        <h2>Access denied</h2>
        <Link className="button" to="/">
          Go home
        </Link>
      </div>
    );
  return children;
}
const ticketPreviews = [
  {
    event: "Saturday walk-in clinic",
    location: "Front desk · Floor 2",
    ticket: "Q-014",
    ahead: 3,
    wait: "~18 min",
  },
  {
    event: "Dinner at your favourite restaurant",
    location: "Table for 2 · Main entrance",
    ticket: "R-028",
    ahead: 2,
    wait: "~24 min",
  },
  {
    event: "Your next salon appointment",
    location: "Hair styling · Reception",
    ticket: "S-009",
    ahead: 1,
    wait: "~9 min",
  },
  {
    event: "Weekend pop-up experience",
    location: "General entry · Gate A",
    ticket: "E-042",
    ahead: 5,
    wait: "~31 min",
  },
];
function TicketPreview() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return undefined;
    const timer = setInterval(
      () => setIndex((current) => (current + 1) % ticketPreviews.length),
      5000,
    );
    return () => clearInterval(timer);
  }, [paused]);
  const preview = ticketPreviews[index];
  return (
    <div className="ticket-preview">
      <div className="preview-top">
        <span className="eyebrow">TICKET PREVIEW</span>
        <button
          type="button"
          className="preview-toggle"
          onClick={() => setPaused((current) => !current)}
          aria-label={paused ? "Resume ticket preview" : "Pause ticket preview"}
        >
          {paused ? "Resume" : "Pause"}
        </button>
      </div>
      <div key={index} className="preview-content">
        <div className="preview-event">
          <QueMeLogo variant="icon" size={42} alt="" aria-hidden="true" />
          <div>
            <strong>{preview.event}</strong>
            <small>{preview.location}</small>
          </div>
        </div>
        <div className="preview-number">{preview.ticket}</div>
        <div className="preview-status">
          <span className="preview-check" aria-hidden="true">
            ✓
          </span>
          <div>
            <strong>You’re in the queue</strong>
            <span>
              {preview.ahead === 1
                ? "1 person ahead of you"
                : `${preview.ahead} people ahead of you`}
            </span>
          </div>
        </div>
        <div className="preview-wait">
          <span>Estimated wait</span>
          <strong>{preview.wait}</strong>
          <span>Updated just now</span>
        </div>
        <div className="preview-footer">
          <span>Illustrative preview</span>
          <span>Your turn, without the wait</span>
        </div>
      </div>
    </div>
  );
}
function Landing() {
  const { user } = useAuth();
  return <LandingPage user={user} TicketPreview={TicketPreview} />;
}
function Auth({ signup = false }) {
  const { user, login, signup: register, googleLogin } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({
    name: "",
    organisation: "",
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const continueWithGoogle = React.useCallback(
    async (credential) => {
      setError("");
      try {
        const u = await googleLogin(credential);
        nav(u.role === "customer" ? "/" : "/manage/events");
      } catch (e) {
        setError(e.message);
      }
    },
    [googleLogin, nav],
  );
  if (user) return <Navigate to="/" replace />;
  async function submit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = signup ? await register(f) : await login(f.email, f.password);
      trackEvent("auth_success", { mode: signup ? "signup" : "login" });
      nav(u.role === "customer" ? "/" : "/manage/events");
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <div className="auth-card">
      <Link className="back-link" to="/">
        <ChevronLeft size={16} /> QueMe
      </Link>
      <div className="eyebrow">{signup ? "START SIMPLE" : "WELCOME BACK"}</div>
      <h1>{signup ? "Make waiting feel lighter." : "Sign in to QueMe."}</h1>
      <p className="muted">
        {signup
          ? "Create an account to join queues, or add an organisation to manage one."
          : "Pick up where you left off."}
      </p>
      {error && <Banner tone="error">{error}</Banner>}
      {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
        <section className="google-auth-panel">
          <div className="google-auth-heading">
            <span className="google-auth-kicker">RECOMMENDED</span>
            <strong>Continue securely with Google</strong>
            <small>Use your Google account to get started faster.</small>
          </div>
          <GoogleAuthButton onSuccess={continueWithGoogle} />
        </section>
      )}
      {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
        <div className="auth-divider">
          <span>or use email instead</span>
        </div>
      )}
      <form onSubmit={submit}>
        {signup && (
          <>
            <label>
              Your name
              <input
                required
                value={f.name}
                onChange={(e) => setF({ ...f, name: e.target.value })}
              />
            </label>
            <label>
              Organisation <span className="optional">optional</span>
              <input
                value={f.organisation}
                onChange={(e) => setF({ ...f, organisation: e.target.value })}
              />
            </label>
          </>
        )}
        <label>
          Email
          <input
            type="email"
            required
            value={f.email}
            onChange={(e) => setF({ ...f, email: e.target.value })}
          />
        </label>
        <label>
          Password
          <input
            type="password"
            minLength="8"
            required
            value={f.password}
            onChange={(e) => setF({ ...f, password: e.target.value })}
          />
        </label>
        <button className="button full" disabled={submitting}>
          {submitting ? "Working…" : signup ? "Create account" : "Sign in"} <ArrowRight size={18} />
        </button>
      </form>
      <p className="switch">
        {signup ? "Already have an account?" : "New to QueMe?"}{" "}
        <Link to={signup ? "/login" : "/signup"}>
          {signup ? "Sign in" : "Create one"}
        </Link>
      </p>
    </div>
  );
}
function EventPage() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api("/events/" + eventId)
      .then((r) => setEvent(r.event))
      .catch((e) => setError(e.message));
  }, [eventId]);
  async function join() {
    if (!user)
      return nav("/login?next=" + encodeURIComponent("/events/" + eventId));
    try {
      const r = await api("/queue/events/" + eventId + "/join", {
        method: "POST",
        idempotencyKey: `join-${eventId}-${user.id}`,
      });
      nav("/tickets/" + r.entry.id);
    } catch (e) {
      setError(e.message);
    }
  }
  if (!event)
    return error ? (
      <div className="page narrow">
        <Banner tone="error">{error}</Banner>
      </div>
    ) : (
      <Loading />
    );
  const unavailable = event.status !== "open";
  const queueStatusLabel =
    event.status === "paused" ? "Queue paused" : "Queue not open";
  const address = event.address || {};
  const addressText =
    [
      event.location,
      address.line1,
      address.area,
      address.city,
      address.state,
      address.postalCode,
    ]
      .filter(Boolean)
      .join(", ") || "Online service";
  const mapQuery =
    event.geo?.coordinates?.length === 2
      ? `${event.geo.coordinates[1]},${event.geo.coordinates[0]}`
      : addressText;
  const encodedMapQuery = encodeURIComponent(mapQuery);
  const mapsUrl = /Android/i.test(navigator.userAgent)
    ? `geo:0,0?q=${encodedMapQuery}`
    : /iPhone|iPad|iPod/i.test(navigator.userAgent)
      ? `maps://?q=${encodedMapQuery}`
      : `https://www.google.com/maps/search/?api=1&query=${encodedMapQuery}`;
  const coverUrl = event.coverImage?.url || event.thumbnailImage?.url;
  const formatDate = (value) =>
    value
      ? new Intl.DateTimeFormat(undefined, {
          weekday: "short",
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
        }).format(new Date(value))
      : null;
  const category =
    event.category && event.category !== "other"
      ? event.category.replaceAll("_", " ")
      : null;
  const modes = (event.participationModes || []).map((mode) =>
    mode.replaceAll("_", " "),
  );
  const requirements = event.customerRequirements || {};
  const accessibility = event.accessibility || {};
  return (
    <div className="page event-detail-page customer-event-page">
      <Link className="back-link" to="/find">
        <ChevronLeft size={16} /> Back to queues
      </Link>
      <div className="event-hero-card">
        <div
          className={"event-cover " + (!coverUrl ? "event-cover-empty" : "")}
        >
          {coverUrl ? (
            <img src={coverUrl} alt={event.name} />
          ) : (
            <QueMeLogo variant="icon" size={64} />
          )}
          <div className="event-cover-shade" />
          <div className="event-cover-label">
            <Pill value={event.status} />
            {category && <span>{category}</span>}
          </div>
        </div>
        <div className="event-hero-copy">
          <div className="event-identity">
            <div className="event-brand-mark">
              <QueMeLogo variant="icon" size={30} />
            </div>
            <div>
              <strong>{event.venueName || "QueMe queue"}</strong>
              <span>{event.timezone || "Local time"}</span>
            </div>
          </div>
          <h1>{event.name}</h1>
          <p>
            {event.shortDescription ||
              event.description ||
              "Join digitally, keep your place, and arrive when it is your turn."}
          </p>
          <div className="event-hero-location">
            <MapPin size={18} />
            <span>{addressText}</span>
            {addressText !== "Online service" && (
              <a className="map-link" href={mapsUrl} target="_blank" rel="noreferrer">
                Open in maps <ArrowRight size={14} />
              </a>
            )}
          </div>
        </div>
      </div>
      <div className="event-detail-grid">
        <section className="event-main">
          <div className="event-live-strip">
            <span className="live-dot" />{" "}
            <strong>{event.waitingCount || 0} people</strong> currently waiting{" "}
            <span>·</span> Updates live
          </div>
          <div className="event-facts">
            <div>
              <Clock3 size={19} />
              <span>
                <strong>Operating hours</strong>
                {event.operatingHours ||
                  (event.weeklyHours?.length
                    ? "See schedule below"
                    : "Hours vary")}
              </span>
            </div>
            <div>
              <CalendarDays size={19} />
              <span>
                <strong>Event timing</strong>
                {formatDate(event.startsAt) || "Open today"}
              </span>
            </div>
            <div>
              <Users size={19} />
              <span>
                <strong>Queue capacity</strong>
                {event.capacity ? `${event.capacity} people` : "No fixed limit"}
              </span>
            </div>
          </div>
          {event.description && (
            <div className="detail-section event-about">
              <h2>About this service</h2>
              <p>{event.description}</p>
            </div>
          )}
          <div className="detail-section">
            <h2>What you’re joining</h2>
            <div className="service-row">
              <span className="service-icon">Q</span>
              <div>
                <strong>{event.shortName || event.name}</strong>
                <p>
                  {modes.length ? modes.join(" · ") : "Virtual queue"} · first
                  come, first served
                </p>
              </div>
              <span className="service-selected">Selected</span>
            </div>
          </div>
          {(event.startsAt || event.endsAt || event.weeklyHours?.length) && (
            <div className="detail-section">
              <h2>When to arrive</h2>
              <div className="schedule-card">
                <CalendarDays size={20} />
                <div>
                  {event.startsAt && (
                    <strong>Starts {formatDate(event.startsAt)}</strong>
                  )}
                  {event.endsAt && <span>Ends {formatDate(event.endsAt)}</span>}
                  {!event.startsAt && event.weeklyHours?.length > 0 && (
                    <strong>Regular operating schedule</strong>
                  )}
                  <small>{event.timezone || "Local time"}</small>
                </div>
              </div>
            </div>
          )}
          {(requirements.instructions ||
            requirements.requiresPhone ||
            requirements.requiresEmail ||
            requirements.maxPartySize > 1) && (
            <div className="detail-section">
              <h2>Before you join</h2>
              <ul className="rules-list">
                {requirements.instructions && (
                  <li>{requirements.instructions}</li>
                )}
                {requirements.requiresPhone && (
                  <li>A phone number is required.</li>
                )}
                {requirements.requiresEmail && (
                  <li>An email address is required.</li>
                )}
                {requirements.maxPartySize > 1 && (
                  <li>
                    Groups up to {requirements.maxPartySize} people are
                    supported.
                  </li>
                )}
              </ul>
            </div>
          )}
          {(accessibility.wheelchairAccessible ||
            accessibility.accessibleParking ||
            accessibility.priorityAssistance ||
            accessibility.notes) && (
            <div className="detail-section">
              <h2>Accessibility</h2>
              <div className="accessibility-card">
                <Accessibility size={21} />
                <div>
                  <strong>Accessibility support available</strong>
                  <p>
                    {[
                      accessibility.wheelchairAccessible &&
                        "Wheelchair accessible",
                      accessibility.accessibleParking && "Accessible parking",
                      accessibility.priorityAssistance && "Priority assistance",
                    ]
                      .filter(Boolean)
                      .join(" · ") || accessibility.notes}
                  </p>
                  {accessibility.notes && <small>{accessibility.notes}</small>}
                </div>
              </div>
            </div>
          )}
          <div className="detail-section">
            <h2>Good to know</h2>
            <ul className="rules-list">
              <li>Stay nearby and keep your ticket page open.</li>
              <li>
                Your place is held digitally; you do not need to stand in line.
              </li>
              <li>We’ll notify you when your turn is approaching.</li>
            </ul>
          </div>
        </section>
        <aside className="join-panel">
          <span className="eyebrow">YOUR NEXT STEP</span>
          <h2>Skip the line.</h2>
          <div className="join-wait">
            <strong>{event.waitingCount || 0}</strong>
            <span>people waiting now</span>
          </div>
          <p className="join-explainer">
            Join from wherever you are. We’ll keep your place and let you know
            when it is time to come over.
          </p>
          {error && <Banner tone="error">{error}</Banner>}
          <button
            className={`button full large ${unavailable ? "queue-unavailable" : ""}`}
            disabled={unavailable}
            onClick={join}
          >
            {unavailable ? queueStatusLabel : "Join the queue"}
            <ArrowRight size={20} />
          </button>
          {unavailable ? (
            <small className="queue-status-note">
              {event.status === "paused"
                ? "The team has paused new joins temporarily."
                : "This queue will be available when the venue opens it."}
            </small>
          ) : (
            <small>
              <ShieldCheck size={14} /> Free to join · Your place is saved
              securely
            </small>
          )}
        </aside>
      </div>
    </div>
  );
}
function TicketPage() {
  const { ticketId } = useParams();
  const [entry, setEntry] = useState(null);
  const [event, setEvent] = useState(null);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const load = useCallback(
    () =>
      api("/queue/tickets/" + ticketId)
        .then((r) => {
          setEntry(r.entry);
          return api("/events/" + r.entry.eventId);
        })
        .then((r) => setEvent(r.event))
        .catch((e) => setError(e.message)),
    [ticketId],
  );
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);
  async function cancel() {
    if (!window.confirm("Leave this queue?")) return;
    try {
      await api("/queue/tickets/" + ticketId + "/cancel", { method: "POST" });
      load();
    } catch (e) {
      setError(e.message);
    }
  }
  async function submitFeedback(eventObject) {
    eventObject.preventDefault();
    try {
      await apiV1("/feedback", {
        method: "POST",
        body: JSON.stringify({
          ticketId: entry.id,
          rating: Number(rating),
          comment,
        }),
      });
      setFeedbackSent(true);
    } catch (e) {
      setError(e.message);
    }
  }
  if (!entry)
    return error ? (
      <div className="page narrow">
        <Banner tone="error">{error}</Banner>
      </div>
    ) : (
      <Loading />
    );
  const ahead = entry.peopleAhead;
  const aheadLabel =
    ahead === 1 ? "person ahead of you" : "people ahead of you";
  return (
    <div className="page ticket-page">
      <div className="connection">
        <span className="dot" /> Connected · Updated just now
      </div>
      {entry.status === "called" && (
        <div className="turn-alert">
          <BellRing size={24} />
          <div>
            <strong>It’s your turn.</strong>
            <span>Head to the service point now.</span>
          </div>
        </div>
      )}
      <div className="ticket-layout">
        <section className="ticket-card">
          <div className="ticket-perforation" />
          <div className="ticket-event">
            <QueMeLogo variant="icon" className="event-logo" size={42} />
            <div>
              <strong>{event?.name || "Your queue"}</strong>
              <small>{event?.location || "Service point"}</small>
            </div>
          </div>
          <span className="eyebrow">YOUR TICKET</span>
          <div className="ticket-number">{entry.ticketNumber}</div>
          <Pill value={entry.status} />
          <div className="ticket-stats">
            <div>
              <strong>{ahead}</strong>
              <span>{aheadLabel}</span>
            </div>
            <div>
              <strong>—</strong>
              <span>Estimate unavailable</span>
            </div>
          </div>
          <div className="ticket-next">
            <strong>What happens next</strong>
            <span>
              {entry.status === "waiting"
                ? "Stay nearby. We’ll update this page when it’s your turn."
                : entry.status === "called"
                  ? "Show this ticket to staff at the service point."
                  : entry.status === "completed"
                    ? "Your visit is complete. Thanks for using QueMe."
                    : "Your service is in progress."}
            </span>
          </div>
          <div className="ticket-meta">
            <span>Connected · updated just now</span>
            <button className="icon-button" onClick={load} aria-label="Refresh">
              <RefreshCw size={17} />
            </button>
          </div>
        </section>
        <aside className="ticket-side">
          <div className="eyebrow">ARRIVAL NOTES</div>
          <h2>Keep this page open.</h2>
          <p className="muted">
            QueMe turn alerts work while this page is open. Browser restrictions
            may pause alerts when closed or backgrounded.
          </p>
          {error && <Banner tone="error">{error}</Banner>}
          {["waiting", "called"].includes(entry.status) && (
            <button
              className="button danger-outline secondary-action"
              onClick={cancel}
            >
              Leave queue
            </button>
          )}
        </aside>
      </div>
      {entry.status === "completed" && !feedbackSent && (
        <section className="feedback-card">
          <div>
            <div className="eyebrow">HOW DID IT GO?</div>
            <h2>Share a quick visit note.</h2>
            <p className="muted">
              Your feedback helps this business improve its queue.
            </p>
          </div>
          <form onSubmit={submitFeedback}>
            <label>
              Rating
              <select
                value={rating}
                onChange={(e) => setRating(e.target.value)}
              >
                <option value="5">5 — Excellent</option>
                <option value="4">4 — Good</option>
                <option value="3">3 — Okay</option>
                <option value="2">2 — Needs work</option>
                <option value="1">1 — Poor</option>
              </select>
            </label>
            <label>
              Comment{" "}
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength="2000"
                placeholder="Optional note"
              />
            </label>
            <button className="button">Send feedback</button>
          </form>
        </section>
      )}
      {feedbackSent && (
        <div className="banner">Thanks for sharing your feedback.</div>
      )}
    </div>
  );
}
function QueueDirectory() {
  const [events, setEvents] = useState([]);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  useEffect(() => {
    api("/events/open")
      .then((r) => setEvents(r.events))
      .catch((e) => setError(e.message));
  }, []);
  const categories = ["All", "Walk-in", "Appointments", "Services"];
  const visible = events
    .filter((e) =>
      (e.name + " " + (e.location || "") + " " + (e.description || ""))
        .toLowerCase()
        .includes(query.toLowerCase()),
    )
    .filter(
      (e) =>
        category === "All" ||
        (e.description || "").toLowerCase().includes(category.toLowerCase()),
    );
  return (
    <div className="page discovery-page">
      <div className="discovery-heading">
        <div>
          <div className="eyebrow">OPEN QUEUES NEAR YOU</div>
          <h1>Find a queue.</h1>
          <p className="muted">
            Choose a queue, join from your phone, and keep your place.
          </p>
        </div>
        <div className="location-control">
          ⌖ <span>All locations</span>
        </div>
      </div>
      <div className="discovery-tools">
        <label className="search-field">
          <span>⌕</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search queues, venues or services"
          />
        </label>
        <div className="category-chips">
          {categories.map((item) => (
            <button
              className={category === item ? "chip active" : "chip"}
              key={item}
              onClick={() => setCategory(item)}
            >
              {item}
            </button>
          ))}
        </div>
      </div>
      {error && <Banner tone="error">{error}</Banner>}
      {visible.length ? (
        <div className="queue-grid">
          {visible.map((e) => (
            <Link className="queue-card" key={e.id} to={"/events/" + e.id}>
              <div className="queue-card-top">
                <Pill value={e.status} />
                <span className="queue-card-mark">Q</span>
              </div>
              <h2>{e.name}</h2>
              <p className="card-organiser">QueMe Demo Workspace</p>
              <div className="card-details">
                <span>⌖ {e.location || "Online queue"}</span>
                <span>◷ {e.operatingHours || "Hours vary"}</span>
              </div>
              <div className="card-footer">
                <strong>{e.waitingCount} waiting</strong>
                <span>Estimate unavailable</span>
                <ArrowRight size={18} />
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="empty discovery-empty">
          <QrCode size={28} />
          <h2>
            {query
              ? "No queues match that search."
              : "No open queues right now."}
          </h2>
          <p>
            {query
              ? "Try a different name, venue or service."
              : "Ask the organiser for a QueMe event link or QR code."}
          </p>
        </div>
      )}
    </div>
  );
}
function QueuePage() {
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(
    () =>
      api("/events/" + eventId + "/queue")
        .then(setData)
        .catch((e) => setError(e.message)),
    [eventId],
  );
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);
  async function act(path, body) {
    try {
      const payload = { ...(body || {}) };
      const ticketMatch = path.match(/tickets\/([^/]+)\/action$/);
      const ticket = ticketMatch
        ? data?.entries?.find((entry) => String(entry.id) === ticketMatch[1])
        : null;
      if (ticket && payload.expectedRevision === undefined)
        payload.expectedRevision = ticket.revision;
      await api(path, {
        method: "POST",
        body: JSON.stringify(payload),
        idempotencyKey: `staff-${path}-${ticket?.revision || Date.now()}`,
      });
      load();
    } catch (e) {
      setError(e.message);
    }
  }
  if (!data)
    return error ? (
      <div className="page">
        <Banner tone="error">{error}</Banner>
      </div>
    ) : (
      <Loading />
    );
  const active = data.entries.find((e) =>
    ["called", "serving"].includes(e.status),
  );
  return (
    <div className="business-page manage">
      <div className="page-heading">
        <div>
          <Link className="back-link" to="/manage/events">
            <ChevronLeft size={16} /> Events
          </Link>
          <div className="eyebrow">QUEUE CONTROL</div>
          <h1>{data.event.name}</h1>
          <p className="muted">
            {data.event.location || "No location"} · {labels[data.event.status]}
          </p>
        </div>
        <Link className="button secondary" to={"/events/" + eventId}>
          <QrCode size={17} /> Customer view
        </Link>
        <Link className="button" to={"/manage/events/" + eventId + "/edit"}>
          <Pencil size={17} /> Edit listing
        </Link>
      </div>
      {error && <Banner tone="error">{error}</Banner>}
      <div className="stat-row">
        {[
          ["waiting", "Waiting"],
          ["called", "Called"],
          ["serving", "Serving"],
          ["completed", "Completed"],
          ["noShows", "No-shows"],
        ].map(([k, l]) => (
          <div
            className={
              k === "completed" || k === "noShows"
                ? "stat stat-secondary"
                : "stat"
            }
            key={k}
          >
            <strong>{data.counts[k] || 0}</strong>
            <span>{l}</span>
          </div>
        ))}
      </div>
      <div className="queue-layout">
        <section className="current-card">
          {active ? (
            <>
              <div className="eyebrow">CURRENT TICKET</div>
              <div className="current-ticket">{active.ticketNumber}</div>
              <Pill value={active.status} />
              <div className="action-row">
                {active.status === "called" && (
                  <button
                    className="button"
                    onClick={() =>
                      act("/queue/tickets/" + active.id + "/action", {
                        action: "start",
                      })
                    }
                  >
                    <Play size={17} /> Start service
                  </button>
                )}
                {active.status === "serving" && (
                  <button
                    className="button"
                    onClick={() =>
                      act("/queue/tickets/" + active.id + "/action", {
                        action: "complete",
                      })
                    }
                  >
                    <Check size={17} /> Complete
                  </button>
                )}
                <button
                  className="button secondary"
                  onClick={() =>
                    act("/queue/tickets/" + active.id + "/action", {
                      action: "recall",
                    })
                  }
                >
                  <BellRing size={17} /> Recall
                </button>
                {active.status === "called" && (
                  <button
                    className="button danger-outline"
                    onClick={() =>
                      act("/queue/tickets/" + active.id + "/action", {
                        action: "no_show",
                      })
                    }
                  >
                    No-show
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="empty-icon">
                <Check />
              </div>
              <h2>No active customer</h2>
              <p className="muted">Call the next person when ready.</p>
              <button
                className="button large"
                disabled={data.event.status === "paused"}
                onClick={() => act("/queue/events/" + eventId + "/call-next")}
              >
                <BellRing size={18} /> Call next
              </button>
            </>
          )}
          <div className="status-controls">
            <span>
              Queue status: <Pill value={data.event.status} />
            </span>
            {data.event.status === "open" && (
              <button
                className="text-button"
                onClick={() =>
                  act("/events/" + eventId + "/status", { status: "paused" })
                }
              >
                <Pause size={16} /> Pause
              </button>
            )}
            {data.event.status === "paused" && (
              <button
                className="text-button"
                onClick={() =>
                  act("/events/" + eventId + "/status", { status: "open" })
                }
              >
                <Play size={16} /> Resume
              </button>
            )}
            {data.event.status === "draft" && (
              <button
                className="text-button"
                onClick={() =>
                  act("/events/" + eventId + "/status", { status: "open" })
                }
              >
                <Play size={16} /> Open queue
              </button>
            )}
          </div>
        </section>
        <section className="list-card">
          <h2>Queue</h2>
          {data.entries
            .filter((e) => ["waiting", "called", "serving"].includes(e.status))
            .map((e) => (
              <div className="queue-row" key={e.id}>
                <b>{e.ticketNumber}</b>
                <span>
                  {e.customerName || "Customer"}
                  <small>
                    {e.status === "waiting"
                      ? e.peopleAhead + " ahead"
                      : labels[e.status]}
                  </small>
                </span>
                <Pill value={e.status} />
              </div>
            ))}
        </section>
      </div>
    </div>
  );
}
function Team() {
  const { user } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const load = useCallback(
    () =>
      Promise.all([
        apiV1(`/tenants/${user.tenantId}/memberships`),
        apiV1(`/tenants/${user.tenantId}/invitations`),
      ]).then(([m, i]) => {
        setMemberships(m.memberships || []);
        setInvitations(i.invitations || []);
      }),
    [user.tenantId],
  );
  useEffect(() => {
    load();
  }, [load]);
  async function submit(e) {
    e.preventDefault();
    setError("");
    setMessage("");
    try {
      const result = await apiV1(`/tenants/${user.tenantId}/invitations`, {
        method: "POST",
        body: JSON.stringify({ email }),
        idempotencyKey: `invite-${email.toLowerCase()}`,
      });
      setEmail("");
      setMessage(
        result.devInvitationToken
          ? `Invitation created. Development acceptance token: ${result.devInvitationToken}`
          : "Invitation created and ready for delivery.",
      );
      load();
    } catch (e) {
      setError(e.message);
    }
  }
  return (
    <div className="page manage">
      <div className="page-heading">
        <div>
          <div className="eyebrow">WORKSPACE / PEOPLE</div>
          <h1>Build a calm, capable team.</h1>
          <p className="muted">
            Invite the people who keep the door moving. Access stays private to
            this workspace.
          </p>
        </div>
        <div className="team-summary">
          <strong>{memberships.length}</strong>
          <span>active members</span>
        </div>
      </div>
      {message && <Banner>{message}</Banner>}
      {error && <Banner tone="error">{error}</Banner>}
      <section className="invite-card">
        <div className="invite-icon">
          <UserPlus size={20} />
        </div>
        <div>
          <h2>Invite a staff member</h2>
          <p className="muted">
            They’ll be able to run queues and view operational configuration.
          </p>
        </div>
        <form onSubmit={submit}>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@business.com"
          />
          <button className="button">
            <Plus size={17} /> Invite
          </button>
        </form>
      </section>
      <div className="team-list">
        {memberships.map((membership) => (
          <div className="team-row" key={membership._id}>
            <div className="member-avatar">
              {(membership.userId?.name || "M").slice(0, 1).toUpperCase()}
            </div>
            <span>
              <strong>{membership.userId?.name || "Member"}</strong>
              <small>{membership.userId?.email}</small>
            </span>
            <Pill
              value={
                membership.role === "owner" ? "organizer" : membership.role
              }
            />
          </div>
        ))}
        {invitations.map((invitation) => (
          <div className="team-row invitation-row" key={invitation._id}>
            <div className="member-avatar pending">
              <Clock3 size={16} />
            </div>
            <span>
              <strong>{invitation.recipient}</strong>
              <small>Invitation · {invitation.role}</small>
            </span>
            <Pill value="waiting" />
          </div>
        ))}
      </div>
    </div>
  );
}
function BusinessOverview() {
  const [events, setEvents] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => {
    api("/events")
      .then((r) => setEvents(r.events))
      .catch((e) => setError(e.message));
  }, []);
  const active = events.filter((e) => ["open", "paused"].includes(e.status));
  const waiting = active.reduce((sum, e) => sum + (e.waitingCount || 0), 0);
  return (
    <div className="business-page">
      <div className="business-page-heading">
        <div>
          <span className="eyebrow">OVERVIEW</span>
          <h1>Good morning.</h1>
          <p className="muted">
            Here’s what needs your attention across your queues.
          </p>
        </div>
        <Link className="button" to="/manage/events/new">
          <Plus size={18} /> New listing
        </Link>
      </div>
      {error && <Banner tone="error">{error}</Banner>}
      <section className="overview-context">
        <div>
          <span className="context-label">CURRENT OPERATING CONTEXT</span>
          <strong>
            {active.length ? "Accepting joins" : "No live queues"}
          </strong>
          <small>
            {active.length
              ? `${active.length} active ${active.length === 1 ? "listing" : "listings"}`
              : "Open a listing to start serving customers."}
          </small>
        </div>
        <span
          className={active.length ? "status-light live" : "status-light"}
        />
      </section>
      <div className="overview-stats">
        <div>
          <span>Waiting customers</span>
          <strong>{waiting}</strong>
          <small>Across active queues</small>
        </div>
        <div>
          <span>Live listings</span>
          <strong>{active.length}</strong>
          <small>Open or paused</small>
        </div>
        <div className="metric-notice">
          <span>Operations note</span>
          <strong>Counter setup is deferred</strong>
          <small>
            Counter assignment will appear when that capability is available.
          </small>
        </div>
      </div>
      <section className="workspace-section">
        <div className="section-heading">
          <div>
            <h2>Queue health</h2>
            <p className="muted">
              Choose a listing to open its live workspace.
            </p>
          </div>
          <Link to="/manage/events">View all</Link>
        </div>
        {events.length ? (
          <div className="overview-list">
            {events.slice(0, 4).map((e) => (
              <Link
                className="overview-list-row"
                key={e.id}
                to={`/manage/events/${e.id}/queue`}
              >
                <span className="listing-mark">Q</span>
                <span className="listing-info">
                  <strong>{e.name}</strong>
                  <small>{e.location || "No location"}</small>
                </span>
                <span className="listing-wait">
                  <b>{e.waitingCount || 0}</b>
                  <small>waiting</small>
                </span>
                <Pill value={e.status} />
                <ArrowRight size={17} />
              </Link>
            ))}
          </div>
        ) : (
          <div className="empty business-empty">
            <h2>Your workspace is ready.</h2>
            <p>Create your first listing to begin.</p>
            <Link className="button" to="/manage/events/new">
              Create listing
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
function BusinessSection({ title, eyebrow, description, children }) {
  return (
    <div className="business-page">
      <div className="business-page-heading">
        <div>
          <span className="eyebrow">{eyebrow}</span>
          <h1>{title}</h1>
          <p className="muted">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
}
function Operations() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const result = await api("/events");
      const queues = await Promise.all(
        (result.events || []).map(async (event) => {
          try {
            const queue = await api(`/events/${event.id}/queue`);
            return { event: queue.event || event, ...queue };
          } catch {
            return { event, entries: [], counts: {} };
          }
        }),
      );
      setRows(queues);
      setError("");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [load]);
  async function callNext(eventId) {
    try {
      await api(`/queue/events/${eventId}/call-next`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      load();
    } catch (e) {
      setError(e.message);
    }
  }
  const live = rows.filter(({ event }) =>
    ["open", "paused"].includes(event.status),
  );
  const waiting = rows.reduce(
    (sum, row) => sum + (row.counts?.waiting || 0),
    0,
  );
  const serving = rows.reduce(
    (sum, row) => sum + (row.counts?.serving || 0),
    0,
  );
  return (
    <div className="business-page operations-page">
      <div className="business-page-heading">
        <div>
          <span className="eyebrow">OPERATIONS / LIVE</span>
          <h1>The room, at a glance.</h1>
          <p className="muted">
            A live view of every queue. This workspace refreshes automatically
            every 5 seconds.
          </p>
        </div>
        <button className="button secondary" onClick={load}>
          <RefreshCw size={17} /> Refresh now
        </button>
      </div>
      {error && <Banner tone="error">{error}</Banner>}
      <section className="live-strip">
        <div>
          <span className="status-light live" />
          <strong>{live.length ? "Live operations" : "No live queues"}</strong>
          <small>
            {live.length} of {rows.length} listings open
          </small>
        </div>
        <span className="live-updated">
          <Activity size={15} /> Auto-refreshing
        </span>
      </section>
      <div className="overview-stats operation-stats">
        <div>
          <span>Waiting</span>
          <strong>{waiting}</strong>
          <small>Customers in line</small>
        </div>
        <div>
          <span>In service</span>
          <strong>{serving}</strong>
          <small>Being served now</small>
        </div>
        <div>
          <span>Active listings</span>
          <strong>{live.length}</strong>
          <small>Open or paused</small>
        </div>
      </div>
      {loading ? (
        <Loading />
      ) : rows.length ? (
        <div className="operation-grid">
          {rows.map((row) => {
            const active = row.entries?.find((entry) =>
              ["called", "serving"].includes(entry.status),
            );
            const canCall =
              row.event.status === "open" &&
              !active &&
              (row.counts?.waiting || 0) > 0;
            return (
              <article className="operation-card" key={row.event.id}>
                <div className="operation-card-head">
                  <div>
                    <span className="eyebrow">
                      {row.event.location || "Listing"}
                    </span>
                    <h2>{row.event.name}</h2>
                  </div>
                  <Pill value={row.event.status} />
                </div>
                <div className="operation-card-metrics">
                  <div>
                    <strong>{row.counts?.waiting || 0}</strong>
                    <span>waiting</span>
                  </div>
                  <div>
                    <strong>{active?.ticketNumber || "—"}</strong>
                    <span>{active ? labels[active.status] : "current"}</span>
                  </div>
                  <div>
                    <strong>{row.counts?.completed || 0}</strong>
                    <span>completed</span>
                  </div>
                </div>
                <div className="operation-card-actions">
                  {canCall && (
                    <button
                      className="button"
                      onClick={() => callNext(row.event.id)}
                    >
                      <BellRing size={16} /> Call next
                    </button>
                  )}
                  <Link
                    className="button secondary"
                    to={`/manage/events/${row.event.id}/queue`}
                  >
                    {active ? "Open queue" : "Manage queue"}{" "}
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="empty">
          <Activity size={28} />
          <h2>Your live room starts here.</h2>
          <p>
            Create a listing, then open its queue to begin serving customers.
          </p>
          <Link className="button" to="/manage/events/new">
            Create listing
          </Link>
        </div>
      )}
    </div>
  );
}
function BusinessSettings() {
  const { user } = useAuth();
  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [preferences, setPreferences] = useState({
    timezone: "UTC",
    language: "en",
    marketingConsent: false,
  });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    Promise.all([apiV1("/users/me"), apiV1("/users/me/preferences")])
      .then(([p, pref]) => {
        setProfile({ name: p.user.name || "", phone: p.user.phone || "" });
        setPreferences((current) => ({
          ...current,
          ...(pref.preferences || {}),
        }));
      })
      .catch((e) => setError(e.message));
  }, []);
  async function saveProfile(event) {
    event.preventDefault();
    setError("");
    setMessage("");
    try {
      await apiV1("/users/me", {
        method: "PATCH",
        body: JSON.stringify(profile),
      });
      setMessage("Profile saved.");
    } catch (e) {
      setError(e.message);
    }
  }
  async function savePreferences(event) {
    event.preventDefault();
    setError("");
    setMessage("");
    try {
      await apiV1("/users/me/preferences", {
        method: "PATCH",
        body: JSON.stringify(preferences),
      });
      setMessage("Preferences saved.");
    } catch (e) {
      setError(e.message);
    }
  }
  return (
    <BusinessSection
      title="Workspace settings"
      eyebrow="SETTINGS"
      description="Keep your business identity and operating defaults up to date."
    >
      <div className="settings-grid">
        <section className="settings-card">
          <h2>Account profile</h2>
          <p className="muted">
            Update the identity used across your QueMe workspace.
          </p>
          <form onSubmit={saveProfile}>
            <label>
              Name
              <input
                value={profile.name}
                onChange={(e) =>
                  setProfile({ ...profile, name: e.target.value })
                }
                required
              />
            </label>
            <label>
              Email
              <input value={user?.email || ""} disabled />
            </label>
            <label>
              Phone
              <input
                value={profile.phone}
                onChange={(e) =>
                  setProfile({ ...profile, phone: e.target.value })
                }
              />
            </label>
            <button className="button">Save profile</button>
          </form>
        </section>
        <section className="settings-card">
          <h2>Preferences</h2>
          <p className="muted">Control how QueMe communicates with you.</p>
          <form onSubmit={savePreferences}>
            <label>
              Timezone
              <select
                value={preferences.timezone}
                onChange={(e) =>
                  setPreferences({ ...preferences, timezone: e.target.value })
                }
              >
                <option>UTC</option>
                <option>Asia/Kolkata</option>
                <option>America/New_York</option>
                <option>Europe/London</option>
              </select>
            </label>
            <label className="check-row">
              <input
                type="checkbox"
                checked={preferences.marketingConsent}
                onChange={(e) =>
                  setPreferences({
                    ...preferences,
                    marketingConsent: e.target.checked,
                  })
                }
              />{" "}
              Product updates and marketing
            </label>
            <button className="button secondary">Save preferences</button>
          </form>
        </section>
      </div>
      {message && <Banner>{message}</Banner>}
      {error && <Banner tone="error">{error}</Banner>}
    </BusinessSection>
  );
}
function BillingPlan() {
  const [data, setData] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const load = useCallback(
    () =>
      apiV1("/billing/current")
        .then(setData)
        .catch((e) => setError(e.message)),
    [],
  );
  useEffect(() => {
    load();
  }, [load]);
  async function changePlan(plan) {
    setError("");
    setMessage("");
    if (!window.confirm(`Start ${plan} billing on the ${cycle} plan?`)) return;
    try {
      const result = await apiV1("/billing/change-plan", {
        method: "POST",
        body: JSON.stringify({
          plan,
          billingCycle: cycle,
          confirmCharge: true,
        }),
      });
      setMessage(result.message);
      load();
    } catch (e) {
      setError(e.message);
    }
  }
  async function cancel() {
    if (!window.confirm("Cancel at the end of the current paid period?"))
      return;
    try {
      const result = await apiV1("/billing/cancel", { method: "POST" });
      setMessage(result.message);
      load();
    } catch (e) {
      setError(e.message);
    }
  }
  return (
    <BusinessSection
      title="Billing & Plan"
      eyebrow="WORKSPACE BILLING"
      description="Your plan belongs to the organisation. Owners and authorised billing administrators manage it here."
    >
      {error && <Banner tone="error">{error}</Banner>}
      {message && <Banner>{message}</Banner>}
      {!data ? (
        <Loading />
      ) : (
        <>
          <div className="billing-summary">
            <div>
              <small>Current plan</small>
              <strong>{data.plan.name}</strong>
              <span>
                {data.subscription.status} · renews{" "}
                {data.subscription.currentPeriodEnd
                  ? new Date(
                      data.subscription.currentPeriodEnd,
                    ).toLocaleDateString()
                  : "—"}
              </span>
            </div>
            <div>
              <small>Tickets this period</small>
              <strong>
                {data.usage.ticketsIssued.toLocaleString()} /{" "}
                {data.usage.allowance.toLocaleString()}
              </strong>
              <span>{data.usage.remaining.toLocaleString()} remaining</span>
            </div>
            <div>
              <small>Billing</small>
              <strong>
                {data.subscription.billingCycle === "annual"
                  ? "Annual"
                  : "Monthly"}
              </strong>
              <span>Taxes shown separately at checkout</span>
            </div>
          </div>
          <section className="settings-card billing-controls">
            <div className="section-heading">
              <div>
                <h2>Change plan</h2>
                <p className="muted">
                  Upgrades apply immediately. Downgrades should be made at
                  renewal after resolving excess resources.
                </p>
              </div>
              <select value={cycle} onChange={(e) => setCycle(e.target.value)}>
                <option value="monthly">Monthly billing</option>
                <option value="annual">Annual billing · two months free</option>
              </select>
            </div>
            <div className="billing-plan-options">
              {["silver", "gold", "platinum"].map((key) => {
                const plan = {
                  silver: [
                    "Silver",
                    "₹499",
                    "1 location · 2 staff · 1,000 tickets",
                  ],
                  gold: [
                    "Gold",
                    "₹1,499",
                    "3 locations · 10 staff · 5,000 tickets",
                  ],
                  platinum: [
                    "Platinum",
                    "₹3,999",
                    "10 locations · 30 staff · 20,000 tickets",
                  ],
                }[key];
                return (
                  <div
                    className={data.subscription.plan === key ? "selected" : ""}
                    key={key}
                  >
                    <h3>{plan[0]}</h3>
                    <strong>
                      {cycle === "annual"
                        ? {
                            silver: "₹4,990",
                            gold: "₹14,990",
                            platinum: "₹39,990",
                          }[key]
                        : plan[1]}
                      <small>/{cycle === "annual" ? "year" : "month"}</small>
                    </strong>
                    <p>{plan[2]}</p>
                    <button
                      className="button"
                      disabled={data.subscription.plan === key}
                      onClick={() => changePlan(key)}
                    >
                      {data.subscription.plan === key
                        ? "Current plan"
                        : `Choose ${plan[0]}`}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
          <section className="settings-card billing-controls">
            <h2>Billing controls</h2>
            <p className="muted">
              Paid access continues until the current period ends. Existing
              tickets continue to be served if usage reaches its allowance.
            </p>
            <button className="text-button danger" onClick={cancel}>
              Cancel at period end
            </button>
          </section>
        </>
      )}
    </BusinessSection>
  );
}
function Reports() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [error, setError] = useState("");
  const [exportMessage, setExportMessage] = useState("");
  useEffect(() => {
    Promise.all([
      apiV1(`/tenants/${user.tenantId}/analytics`),
      apiV1(`/tenants/${user.tenantId}/audit-events?limit=12`),
    ])
      .then(([analytics, events]) => {
        setData(analytics);
        setAudit(events.events || []);
      })
      .catch((e) => setError(e.message));
  }, [user.tenantId]);
  const counts = data?.counts || {};
  async function requestExport() {
    try {
      const response = await apiV1(`/tenants/${user.tenantId}/exports`, {
        method: "POST",
        body: JSON.stringify({ filters: { scope: "queue-history" } }),
      });
      setExportMessage(`Export ${response.job._id} queued.`);
    } catch (e) {
      setError(e.message);
    }
  }
  return (
    <BusinessSection
      title="Queue reporting"
      eyebrow="REPORTS"
      description="A fresh operational snapshot from committed ticket records."
    >
      {error && <Banner tone="error">{error}</Banner>}
      {exportMessage && <Banner>{exportMessage}</Banner>}
      {data ? (
        <>
          <div className="reports-toolbar">
            <span className="toolbar-note">
              <Check size={15} /> Live snapshot ·{" "}
              {data.generatedAt
                ? new Date(data.generatedAt).toLocaleTimeString()
                : "just now"}
            </span>
            <button className="button secondary" onClick={requestExport}>
              <QrCode size={16} /> Request data export
            </button>
          </div>
          <div className="overview-stats">
            <div>
              <span>Waiting</span>
              <strong>{counts.waiting || 0}</strong>
              <small>Current queue load</small>
            </div>
            <div>
              <span>Completed</span>
              <strong>{counts.completed || 0}</strong>
              <small>Finished visits</small>
            </div>
            <div>
              <span>No-shows</span>
              <strong>{counts.no_show || 0}</strong>
              <small>Needs follow-up</small>
            </div>
          </div>
          <section className="list-card audit-card">
            <div className="section-heading">
              <div>
                <h2>Recent activity</h2>
                <p className="muted">
                  Immutable actions recorded by the queue engine.
                </p>
              </div>
            </div>
            {audit.length ? (
              audit.map((event) => (
                <div className="audit-row" key={event._id}>
                  <span>
                    <strong>{event.action}</strong>
                    <small>
                      {event.resourceType} · revision {event.newRevision ?? "—"}
                    </small>
                  </span>
                  <time>{new Date(event.createdAt).toLocaleString()}</time>
                </div>
              ))
            ) : (
              <p className="muted">No audit activity yet.</p>
            )}
          </section>
        </>
      ) : (
        <Loading />
      )}
    </BusinessSection>
  );
}
function Configuration() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [locationName, setLocationName] = useState("");
  const [serviceName, setServiceName] = useState("");
  const [counterName, setCounterName] = useState("");
  const [queueName, setQueueName] = useState("");
  const [sessionCapacity, setSessionCapacity] = useState(50);
  const [businessName, setBusinessName] = useState("");
  const [businessDescription, setBusinessDescription] = useState("");
  const load = useCallback(
    () =>
      Promise.all([
        apiV1(`/tenants/${user.tenantId}/business`),
        apiV1(`/tenants/${user.tenantId}/locations`),
        apiV1(`/tenants/${user.tenantId}/services`),
        apiV1(`/tenants/${user.tenantId}/queues`),
        apiV1(`/tenants/${user.tenantId}/queue-sessions`),
        apiV1(`/tenants/${user.tenantId}/counters`),
        apiV1(`/tenants/${user.tenantId}/policy-versions`),
        apiV1(`/tenants/${user.tenantId}/outbox-status`),
      ])
        .then(
          ([
            business,
            locations,
            services,
            queues,
            sessions,
            counters,
            policies,
            outbox,
          ]) =>
            setData({
              business: business.business,
              locations: locations.locations,
              services: services.services,
              queues: queues.queues,
              sessions: sessions.sessions,
              counters: counters.counters,
              policies: policies.policies,
              outbox,
            }),
        )
        .catch((e) => setError(e.message)),
    [user.tenantId],
  );
  useEffect(() => {
    load();
  }, [load]);
  async function create(path, payload, clear) {
    setError("");
    setMessage("");
    try {
      await apiV1(path, { method: "POST", body: JSON.stringify(payload) });
      clear("");
      setMessage("Saved.");
      load();
    } catch (e) {
      setError(e.message);
    }
  }
  async function saveBusiness(event) {
    event.preventDefault();
    setError("");
    setMessage("");
    try {
      await apiV1(`/tenants/${user.tenantId}/business`, {
        method: "PUT",
        body: JSON.stringify({
          publicName: businessName,
          description: businessDescription,
        }),
      });
      setMessage("Business profile saved.");
      load();
    } catch (e) {
      setError(e.message);
    }
  }
  if (!data)
    return error ? (
      <div className="page">
        <Banner tone="error">{error}</Banner>
      </div>
    ) : (
      <Loading />
    );
  return (
    <BusinessSection
      title="Workspace configuration"
      eyebrow="CONFIGURATION"
      description="Connect business details, services, queues, sessions, counters, and delivery health."
    >
      {message && <Banner>{message}</Banner>}
      {error && <Banner tone="error">{error}</Banner>}
      <section className="config-profile">
        <div className="config-profile-icon">
          <Building2 size={22} />
        </div>
        <div className="config-profile-copy">
          <span className="eyebrow">PUBLIC PROFILE</span>
          <h2>{data.business?.publicName || "Set up your business profile"}</h2>
          <p className="muted">
            This is the identity customers see before they join a queue.
          </p>
        </div>
        <form onSubmit={saveBusiness}>
          <label>
            Business name
            <input
              value={businessName || data.business?.publicName || ""}
              onChange={(e) => setBusinessName(e.target.value)}
              required
            />
          </label>
          <label>
            Description
            <input
              value={businessDescription || data.business?.description || ""}
              onChange={(e) => setBusinessDescription(e.target.value)}
              placeholder="A short description"
            />
          </label>
          <button className="button">Save profile</button>
        </form>
      </section>
      <div className="overview-stats">
        <div>
          <span>Locations</span>
          <strong>{data.locations.length}</strong>
          <small>Operating places</small>
        </div>
        <div>
          <span>Services</span>
          <strong>{data.services.length}</strong>
          <small>Customer offerings</small>
        </div>
        <div>
          <span>Outbox pending</span>
          <strong>{data.outbox.pending}</strong>
          <small>Durable events to deliver</small>
        </div>
      </div>
      <div className="settings-grid">
        <section className="settings-card">
          <h2>Add a location</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              create(
                `/tenants/${user.tenantId}/locations`,
                {
                  name: locationName,
                  businessId: data.business?._id,
                  timezone: "UTC",
                },
                setLocationName,
              );
            }}
          >
            <label>
              Name
              <input
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                required
              />
            </label>
            <button className="button">
              <Plus size={17} /> Add location
            </button>
          </form>
        </section>
        <section className="settings-card">
          <h2>Add a service</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              create(
                `/tenants/${user.tenantId}/services`,
                { name: serviceName, businessId: data.business?._id },
                setServiceName,
              );
            }}
          >
            <label>
              Name
              <input
                value={serviceName}
                onChange={(e) => setServiceName(e.target.value)}
                required
              />
            </label>
            <button className="button">
              <Plus size={17} /> Add service
            </button>
          </form>
        </section>
      </div>
      <div className="settings-grid">
        <section className="settings-card">
          <h2>Create a queue</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              create(
                `/tenants/${user.tenantId}/queues`,
                {
                  name: queueName,
                  locationId: data.locations[0]?._id,
                  serviceIds: data.services.map((service) => service._id),
                },
                setQueueName,
              );
            }}
          >
            <label>
              Name
              <input
                value={queueName}
                onChange={(e) => setQueueName(e.target.value)}
                placeholder="General admission"
                required
              />
            </label>
            <button className="button">
              <Plus size={17} /> Create queue
            </button>
          </form>
        </section>
        <section className="settings-card">
          <h2>Create a session</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!data.queues[0]) return setError("Create a queue first.");
              create(
                `/tenants/${user.tenantId}/queue-sessions`,
                {
                  queueId: data.queues[0]._id,
                  capacity: Number(sessionCapacity),
                },
                setSessionCapacity,
              );
            }}
          >
            <label>
              Capacity
              <input
                type="number"
                min="1"
                value={sessionCapacity}
                onChange={(e) => setSessionCapacity(e.target.value)}
                required
              />
            </label>
            <button className="button secondary">
              <Play size={16} /> Create session
            </button>
          </form>
        </section>
      </div>
      <section className="list-card audit-card">
        <div className="section-heading">
          <div>
            <h2>Operational resources</h2>
            <p className="muted">
              Fetched from the server and ready to use across your workspace.
            </p>
          </div>
        </div>
        <div className="resource-grid">
          <div>
            <strong>{data.queues.length}</strong>
            <span>Queues</span>
          </div>
          <div>
            <strong>{data.sessions.length}</strong>
            <span>Sessions</span>
          </div>
          <div>
            <strong>{data.counters.length}</strong>
            <span>Counters</span>
          </div>
          <div>
            <strong>{data.policies.length}</strong>
            <span>Policy versions</span>
          </div>
        </div>
        {data.counters.length === 0 && (
          <form
            className="inline-create"
            onSubmit={(e) => {
              e.preventDefault();
              create(
                `/tenants/${user.tenantId}/counters`,
                { name: counterName },
                setCounterName,
              );
            }}
          >
            <input
              placeholder="Counter name"
              value={counterName}
              onChange={(e) => setCounterName(e.target.value)}
              required
            />
            <button className="button secondary">
              <Plus size={16} /> Add counter
            </button>
          </form>
        )}
      </section>
    </BusinessSection>
  );
}
function Notifications() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushMessage, setPushMessage] = useState("");
  const load = useCallback(
    () =>
      apiV1("/notifications?limit=50")
        .then((r) => setItems(r.notifications || []))
        .catch((e) => setError(e.message)),
    [],
  );
  useEffect(() => {
    load();
    hasPushSubscription()
      .then(setPushEnabled)
      .catch(() => {});
  }, [load]);
  async function turnOnPush() {
    setError("");
    setPushMessage("");
    try {
      await enablePush();
      setPushEnabled(true);
      setPushMessage("Push notifications are enabled on this device.");
    } catch (e) {
      setError(e.message);
    }
  }
  async function sendTestPush() {
    try {
      await apiV1("/push/test", { method: "POST" });
      setPushMessage("Test notification sent.");
    } catch (e) {
      setError(e.message);
    }
  }
  async function markRead(id) {
    await apiV1(`/notifications/${id}/read`, { method: "PATCH" });
    load();
  }
  return (
    <div className="page manage">
      <div className="page-heading">
        <div>
          <div className="eyebrow">INBOX</div>
          <h1>Notifications</h1>
          <p className="muted">Updates tied to your queue activity.</p>
        </div>
      </div>
      {error && <Banner tone="error">{error}</Banner>}
      {pushMessage && <Banner>{pushMessage}</Banner>}
      <section className="push-card">
        <div className="push-card-icon">
          <BellRing size={20} />
        </div>
        <div>
          <h2>Queue alerts on this device</h2>
          <p className="muted">
            Push alerts are enabled automatically after sign-in when your
            browser allows them.
          </p>
        </div>
        {pushEnabled ? (
          <button className="button secondary" onClick={sendTestPush}>
            Send test
          </button>
        ) : (
          <button className="button" onClick={turnOnPush}>
            Enable notifications
          </button>
        )}
      </section>
      {items.length ? (
        <div className="event-list">
          {items.map((item) => (
            <div
              className={`manage-event notification-row ${item.read ? "read" : ""}`}
              key={item._id}
            >
              <span>
                <strong>{item.message}</strong>
                <small className="muted">
                  {new Date(item.createdAt || item.timestamp).toLocaleString()}
                </small>
              </span>
              {!item.read && (
                <button
                  className="text-button"
                  onClick={() => markRead(item._id)}
                >
                  Mark read
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty">
          <h2>All clear.</h2>
          <p>No notifications yet.</p>
        </div>
      )}
    </div>
  );
}
function CustomerTickets() {
  const [tickets, setTickets] = useState([]);
  const [error, setError] = useState("");
  const load = useCallback(
    () =>
      api("/queue/tickets?active=true&limit=25")
        .then((r) => setTickets(r.tickets || []))
        .catch((e) => setError(e.message)),
    [],
  );
  useEffect(() => {
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [load]);
  return (
    <Guard>
      <div className="page narrow tickets-page">
        <div className="tickets-heading">
          <div className="tickets-heading-top">
            <div className="eyebrow">MY TICKETS</div>
            <span className="tickets-live">
              <span className="live-dot" /> Live updates
            </span>
          </div>
          <h1>Your active tickets.</h1>
          <p className="muted">
            Keep your place visible and know exactly when it’s time to move.
          </p>
        </div>
        {error && <Banner tone="error">{error}</Banner>}
        {tickets.length ? (
          <>
            <div className="tickets-summary">
              <div>
                <strong>{tickets.length}</strong>
                <span>
                  active {tickets.length === 1 ? "ticket" : "tickets"}
                </span>
              </div>
              <div>
                <strong>
                  {
                    tickets.filter((ticket) => ticket.status === "called")
                      .length
                  }
                </strong>
                <span>ready now</span>
              </div>
              <span className="tickets-summary-note">
                Updated automatically
              </span>
            </div>
            <div className="ticket-list">
              {tickets.map((ticket) => (
                <Link
                  className="ticket-list-item"
                  key={ticket.id}
                  to={`/tickets/${ticket.id}`}
                >
                  <span className="ticket-list-mark">Q</span>
                  <span className="ticket-list-copy">
                    <strong>{ticket.ticketNumber}</strong>
                    <small>{labels[ticket.status] || ticket.status}</small>
                  </span>
                  <span className="ticket-list-status">
                    <Pill value={ticket.status} />
                    <small>
                      {ticket.status === "waiting"
                        ? `${ticket.peopleAhead || 0} ahead`
                        : ticket.status === "called"
                          ? "Head over now"
                          : "In progress"}
                    </small>
                  </span>
                  <ArrowRight className="ticket-list-arrow" size={20} />
                </Link>
              ))}
            </div>
          </>
        ) : (
          <div className="empty tickets-empty">
            <div className="empty-icon" aria-hidden="true">
              <QrCode size={25} strokeWidth={2.2} />
            </div>
            <h2>No active tickets</h2>
            <p>Explore open queues to find your next visit.</p>
            <Link className="button" to="/find">
              Explore queues
            </Link>
          </div>
        )}
      </div>
    </Guard>
  );
}
export default function App() {
  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/find" element={<QueueDirectory />} />
        <Route
          path="/notifications"
          element={
            <Guard>
              <Notifications />
            </Guard>
          }
        />
        <Route path="/login" element={<Auth />} />
        <Route path="/LoginPage" element={<Auth />} />
        <Route path="/signup" element={<Auth signup />} />
        <Route path="/events/:eventId" element={<EventPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
        <Route path="/terms" element={<Navigate to="/terms-and-conditions" replace />} />
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="/tickets" element={<CustomerTickets />} />
        <Route
          path="/tickets/:ticketId"
          element={
            <Guard>
              <TicketPage />
            </Guard>
          }
        />
        <Route
          path="/manage"
          element={
            <Guard roles={["organizer", "staff"]}>
              <BusinessOverview />
            </Guard>
          }
        />
        <Route
          path="/manage/events"
          element={
            <Guard roles={["organizer", "staff"]}>
              <Events />
            </Guard>
          }
        />
        <Route
          path="/manage/events/new"
          element={
            <Guard roles={["organizer"]}>
              <EventForm />
            </Guard>
          }
        />
        <Route
          path="/manage/events/:eventId/edit"
          element={
            <Guard roles={["organizer"]}>
              <EventForm />
            </Guard>
          }
        />
        <Route
          path="/manage/events/:eventId/queue"
          element={
            <Guard roles={["organizer", "staff"]}>
              <QueuePage />
            </Guard>
          }
        />
        <Route
          path="/manage/operations"
          element={
            <Guard roles={["organizer", "staff"]}>
              <Operations />
            </Guard>
          }
        />
        <Route
          path="/manage/team"
          element={
            <Guard roles={["organizer"]}>
              <Team />
            </Guard>
          }
        />
        <Route
          path="/manage/reports"
          element={
            <Guard roles={["organizer"]}>
              <Reports />
            </Guard>
          }
        />
        <Route
          path="/manage/configuration"
          element={
            <Guard roles={["organizer", "staff"]}>
              <Configuration />
            </Guard>
          }
        />
        <Route
          path="/manage/settings"
          element={
            <Guard roles={["organizer"]}>
              <BusinessSettings />
            </Guard>
          }
        />
        <Route
          path="/manage/billing"
          element={
            <Guard roles={["organizer"]}>
              <BillingPlan />
            </Guard>
          }
        />
        <Route path="/business" element={<Navigate to="/manage" replace />} />
        <Route path="/dashboard" element={<Navigate to="/manage" replace />} />
        <Route path="/events" element={<Navigate to="/find" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Shell>
  );
}
